import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Sidebar as SidebarIcon } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { OverlayWrapper } from './overlay'
import { Sheet, SheetOverlay } from './sheet'
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip'

/**
 * Sidebar — a composable app-navigation sidebar (provider + container + menu
 * primitives), adopted from the shadcn/TailGrids sidebar model and wired to First
 * Bite tokens. `SidebarProvider` owns the open/collapsed state (controlled or
 * uncontrolled), tracks mobile, and binds the ⌘/Ctrl-B toggle shortcut. On mobile
 * the sidebar becomes an off-canvas `Sheet`; on desktop it expands/collapses (to
 * icons or fully off-canvas) with a width transition.
 *
 *   <SidebarProvider>
 *     <Sidebar collapsible="icon">
 *       <SidebarHeader>…</SidebarHeader>
 *       <SidebarContent>
 *         <SidebarGroup>
 *           <SidebarGroupLabel>Platform</SidebarGroupLabel>
 *           <SidebarGroupContent>
 *             <SidebarMenu>
 *               <SidebarMenuItem>
 *                 <SidebarMenuButton isActive tooltip="Dashboard">
 *                   <House /> <span>Dashboard</span>
 *                 </SidebarMenuButton>
 *               </SidebarMenuItem>
 *             </SidebarMenu>
 *           </SidebarGroupContent>
 *         </SidebarGroup>
 *       </SidebarContent>
 *       <SidebarFooter>…</SidebarFooter>
 *     </Sidebar>
 *     <main className="flex-1">
 *       <SidebarTrigger />
 *       …
 *     </main>
 *   </SidebarProvider>
 *
 * Put the label in a `<span>` inside `SidebarMenuButton`: in `icon`-collapsed mode
 * spans are hidden and the `tooltip` takes over. State is in-memory (no cookie
 * persistence — that's an app/SSR concern). The `inset` variant styles the
 * sidebar; pairing it with an inset main panel is left to the consumer.
 */

const SIDEBAR_WIDTH = '16rem'
const SIDEBAR_WIDTH_ICON = '3rem'

type SidebarState = 'expanded' | 'collapsed'

interface SidebarContextValue {
  state: SidebarState
  open: boolean
  setOpen: (open: boolean) => void
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  isMobile: boolean
  toggleSidebar: () => void
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null)

/** Read sidebar state + toggles. Must be used within `SidebarProvider`. */
export const useSidebar = (): SidebarContextValue => {
  const ctx = React.useContext(SidebarContext)
  if (!ctx) throw new Error('useSidebar must be used within a <SidebarProvider>')
  return ctx
}

export interface SidebarProviderProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** Initial open state (uncontrolled). Default true. */
  defaultOpen?: boolean
  /** Controlled open state. Pair with `onOpenChange`. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export const SidebarProvider = ({
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  className,
  style,
  children,
  ...props
}: SidebarProviderProps) => {
  const [isMobile, setIsMobile] = React.useState(false)
  const [openMobile, setOpenMobile] = React.useState(false)

  React.useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)')
    const onChange = () => setIsMobile(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  const isControlled = openProp !== undefined
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen)
  const open = isControlled ? openProp : internalOpen
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) setOpenMobile((o) => !o)
    else setOpen(!open)
  }, [isMobile, open, setOpen])

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'b' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        toggleSidebar()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleSidebar])

  const ctx = React.useMemo<SidebarContextValue>(
    () => ({
      state: open ? 'expanded' : 'collapsed',
      open,
      setOpen,
      openMobile,
      setOpenMobile,
      isMobile,
      toggleSidebar,
    }),
    [open, setOpen, openMobile, isMobile, toggleSidebar]
  )

  return (
    <SidebarContext.Provider value={ctx}>
      <div
        style={
          {
            '--fbui-sidebar-width': SIDEBAR_WIDTH,
            '--fbui-sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn('group/sidebar-wrapper flex w-full', className)}
        {...props}
      >
        {children}
      </div>
    </SidebarContext.Provider>
  )
}
SidebarProvider.displayName = 'SidebarProvider'

type Collapsible = 'offcanvas' | 'icon' | 'none'
type SidebarVariant = 'sidebar' | 'floating' | 'inset'

interface SidebarConfig {
  collapsible: Collapsible
  variant: SidebarVariant
  side: 'left' | 'right'
}
const SidebarConfigContext = React.createContext<SidebarConfig>({
  collapsible: 'offcanvas',
  variant: 'sidebar',
  side: 'left',
})

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'left' | 'right'
  variant?: SidebarVariant
  collapsible?: Collapsible
  /** Show the close button on the mobile sheet. Default true. */
  showSheetCloseButton?: boolean
}

const variantClass: Record<SidebarVariant, string> = {
  sidebar: 'border-fbui-gray-200',
  floating: 'm-2 rounded-xl border border-fbui-gray-200 shadow-fbui-md',
  inset: 'border-transparent',
}

export const Sidebar = ({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  showSheetCloseButton = true,
  className,
  children,
  ...props
}: SidebarProps) => {
  const { isMobile, openMobile, setOpenMobile, state } = useSidebar()
  const config = React.useMemo<SidebarConfig>(
    () => ({ collapsible, variant, side }),
    [collapsible, variant, side]
  )

  const inner = (
    <SidebarConfigContext.Provider value={config}>
      <div
        data-sidebar="sidebar"
        className="flex h-full w-full flex-col bg-fbui-base-white text-fbui-gray-900"
      >
        {children}
      </div>
    </SidebarConfigContext.Provider>
  )

  // Mobile: render the sidebar as an off-canvas Sheet driven by the provider.
  if (isMobile) {
    return (
      <OverlayWrapper isOpen={openMobile} onOpenChange={setOpenMobile}>
        <SheetOverlay side={side} className="w-[17rem] max-w-[85vw]">
          <Sheet
            showCloseButton={showSheetCloseButton}
            aria-label="Sidebar"
            className="p-0"
          >
            {inner}
          </Sheet>
        </SheetOverlay>
      </OverlayWrapper>
    )
  }

  const widthClass =
    collapsible === 'none' || state === 'expanded'
      ? 'w-[var(--fbui-sidebar-width)]'
      : collapsible === 'icon'
        ? 'w-[var(--fbui-sidebar-width-icon)]'
        : 'w-0'

  return (
    <div
      className="group/sidebar hidden md:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
    >
      <div
        className={cn(
          'h-full overflow-hidden transition-[width] duration-200 ease-out',
          side === 'left' ? 'border-r' : 'border-l',
          variantClass[variant],
          widthClass,
          className
        )}
        {...props}
      >
        {inner}
      </div>
    </div>
  )
}
Sidebar.displayName = 'Sidebar'

export interface SidebarTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

export const SidebarTrigger = ({
  className,
  onClick,
  ...props
}: SidebarTriggerProps) => {
  const { toggleSidebar } = useSidebar()
  return (
    <button
      type="button"
      data-sidebar="trigger"
      aria-label="Toggle sidebar"
      onClick={(e) => {
        onClick?.(e)
        toggleSidebar()
      }}
      className={cn(
        'inline-flex size-8 items-center justify-center rounded-md text-fbui-gray-700 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-900 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand [&_svg]:size-5',
        className
      )}
      {...props}
    >
      <SidebarIcon aria-hidden="true" />
    </button>
  )
}
SidebarTrigger.displayName = 'SidebarTrigger'

export const SidebarHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-sidebar="header"
    className={cn('flex flex-col gap-2 p-2', className)}
    {...props}
  />
)
SidebarHeader.displayName = 'SidebarHeader'

export const SidebarContent = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-sidebar="content"
    className={cn(
      'fbui-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-2',
      // Hide overflow in icon mode so collapsing labels don't peek.
      'group-data-[collapsible=icon]/sidebar:overflow-hidden',
      className
    )}
    {...props}
  />
)
SidebarContent.displayName = 'SidebarContent'

export const SidebarFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-sidebar="footer"
    className={cn('flex flex-col gap-2 p-2', className)}
    {...props}
  />
)
SidebarFooter.displayName = 'SidebarFooter'

export const SidebarGroup = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-sidebar="group"
    className={cn('relative flex w-full min-w-0 flex-col', className)}
    {...props}
  />
)
SidebarGroup.displayName = 'SidebarGroup'

export const SidebarGroupLabel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-sidebar="group-label"
    className={cn(
      'flex h-8 shrink-0 items-center px-2 text-xs font-medium text-fbui-text-secondary',
      // Fade + collapse away in icon mode.
      'transition-opacity group-data-[collapsible=icon]/sidebar:h-0 group-data-[collapsible=icon]/sidebar:opacity-0',
      className
    )}
    {...props}
  />
)
SidebarGroupLabel.displayName = 'SidebarGroupLabel'

export const SidebarGroupContent = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-sidebar="group-content"
    className={cn('w-full text-sm', className)}
    {...props}
  />
)
SidebarGroupContent.displayName = 'SidebarGroupContent'

export const SidebarMenu = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLUListElement>) => (
  <ul
    data-sidebar="menu"
    className={cn('flex w-full min-w-0 flex-col gap-1', className)}
    {...props}
  />
)
SidebarMenu.displayName = 'SidebarMenu'

export const SidebarMenuItem = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLLIElement>) => (
  <li
    data-sidebar="menu-item"
    className={cn('group/menu-item relative', className)}
    {...props}
  />
)
SidebarMenuItem.displayName = 'SidebarMenuItem'

const menuButtonVariants = cva(
  'group/menu-button peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md px-2 text-left text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0 ' +
    // Icon-collapsed: square the button, center the icon, hide label spans.
    'group-data-[collapsible=icon]/sidebar:!size-8 group-data-[collapsible=icon]/sidebar:!justify-center group-data-[collapsible=icon]/sidebar:!p-0 group-data-[collapsible=icon]/sidebar:[&>span]:hidden',
  {
    variants: {
      variant: {
        default: 'hover:bg-fbui-gray-100 hover:text-fbui-gray-900',
        outline:
          'border border-fbui-gray-200 bg-fbui-base-white hover:bg-fbui-gray-100',
      },
      size: {
        default: 'h-8',
        sm: 'h-7 text-xs',
        lg: 'h-10',
      },
      isActive: {
        true: 'bg-fbui-accent font-medium text-fbui-accent-foreground hover:bg-fbui-accent',
        false: 'text-fbui-gray-700',
      },
    },
    defaultVariants: { variant: 'default', size: 'default', isActive: false },
  }
)

export interface SidebarMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof menuButtonVariants>, 'isActive'> {
  /** Active/selected nav state. */
  isActive?: boolean
  /** Render the child element as the button (e.g. an `<a>`/`<Link>`). */
  asChild?: boolean
  /** Label shown in a tooltip when the sidebar is collapsed to icons. */
  tooltip?: React.ReactNode
}

export const SidebarMenuButton = ({
  className,
  variant,
  size = 'default',
  isActive = false,
  asChild = false,
  tooltip,
  ...props
}: SidebarMenuButtonProps) => {
  const { isMobile, state } = useSidebar()
  const { collapsible } = React.useContext(SidebarConfigContext)
  const Comp = asChild ? Slot : 'button'
  const button = (
    <Comp
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        menuButtonVariants({ variant, size, isActive }),
        className
      )}
      {...props}
    />
  )

  const showTooltip =
    tooltip != null &&
    !isMobile &&
    state === 'collapsed' &&
    collapsible === 'icon'
  if (!showTooltip) return button

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right">{tooltip}</TooltipContent>
    </Tooltip>
  )
}
SidebarMenuButton.displayName = 'SidebarMenuButton'

export const SidebarMenuSub = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLUListElement>) => (
  <ul
    data-sidebar="menu-sub"
    className={cn(
      'mx-3.5 flex min-w-0 flex-col gap-1 border-l border-fbui-gray-200 px-2.5 py-0.5',
      'group-data-[collapsible=icon]/sidebar:hidden',
      className
    )}
    {...props}
  />
)
SidebarMenuSub.displayName = 'SidebarMenuSub'

export const SidebarMenuSubItem = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLLIElement>) => (
  <li data-sidebar="menu-sub-item" className={className} {...props} />
)
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem'

export interface SidebarMenuSubButtonProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  isActive?: boolean
  asChild?: boolean
}

export const SidebarMenuSubButton = ({
  className,
  isActive = false,
  asChild = false,
  ...props
}: SidebarMenuSubButtonProps) => {
  const Comp = asChild ? Slot : 'a'
  return (
    <Comp
      data-sidebar="menu-sub-button"
      data-active={isActive}
      className={cn(
        'flex h-7 min-w-0 items-center gap-2 rounded-md px-2 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand [&>svg]:size-4 [&>svg]:shrink-0',
        isActive
          ? 'bg-fbui-accent font-medium text-fbui-accent-foreground'
          : 'text-fbui-gray-700 hover:bg-fbui-gray-100 hover:text-fbui-gray-900',
        'group-data-[collapsible=icon]/sidebar:hidden',
        className
      )}
      {...props}
    />
  )
}
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton'
